import React from "react";
import { Link } from "react-router-dom";
import { MdChat } from "react-icons/md";
import { useAppLanguage } from "hooks/useAppLanguage";
import EmptyState from "components/ui/EmptyState";
import { Skeleton } from "components/ui/Skeleton";
import UserAvatar from "components/ui/UserAvatar";
import {
  directChatService,
  type DirectChatInboxItem,
} from "services/directChatService";
import { formatLocaleDateTime } from "lib/localeFormat";

function formatWhen(iso: string | null, isArabic: boolean): string {
  if (!iso) return "";
  return formatLocaleDateTime(iso, isArabic, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function MessagesInboxPage() {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const [loading, setLoading] = React.useState(true);
  const [items, setItems] = React.useState<DirectChatInboxItem[]>([]);

  const reload = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await directChatService.listConversations();
      setItems(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <div className="w-full space-y-5 p-6">
      <div>
        <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
          {isArabic ? "الرسائل" : "Messages"}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {isArabic
            ? "محادثات خاصة 1:1 مع طلابك المسجّلين في كورساتك."
            : "Private 1:1 chats with students enrolled in your courses."}
        </p>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-navy-800">
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-16 w-full rounded-xl" />
            <Skeleton className="h-16 w-full rounded-xl" />
            <Skeleton className="h-16 w-full rounded-xl" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<MdChat className="h-6 w-6" />}
            title={isArabic ? "لا توجد محادثات بعد" : "No conversations yet"}
            description={
              isArabic
                ? "افتح صفحة طالب واضغط «رسالة» لبدء محادثة خاصة."
                : "Open a student profile and tap Message to start a private chat."
            }
          />
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-white/10">
            {items.map((item) => {
              const id = item.conversation_id;
              if (!id) return null;
              return (
                <li key={id}>
                  <Link
                    to={`/instructor/messages/${id}`}
                    className="flex items-center gap-3 px-2 py-3 hover:bg-gray-50 dark:hover:bg-white/5"
                  >
                    <UserAvatar
                      src={item.peer.avatar_url}
                      alt={item.peer.name || ""}
                      className="h-11 w-11 rounded-full object-cover"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="truncate text-sm font-semibold text-gray-800 dark:text-white">
                          {item.peer.name || (isArabic ? "طالب" : "Student")}
                        </p>
                        <span className="shrink-0 text-[11px] text-gray-400">
                          {formatWhen(item.last_message_at, isArabic)}
                        </span>
                      </div>
                      <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                        {item.last_message_preview ||
                          (isArabic ? "ابدأ المحادثة" : "Start chatting")}
                      </p>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
